// Integrations Console (M8.5 + M8.6).
//
// Per-LegalEntity view: lists every configurable integration with a
// status badge, "Configure" / "Edit" / "Test" actions. The Configure
// modal collects required + optional credential fields and POSTs to
// the vault-backed upsert endpoint.
//
// LE selection: first active legal entity in the tenant.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Settings, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { IntegrationConfigureDrawer } from "../components/integrations/IntegrationConfigureDrawer";
import { api } from "../lib/api";
import type { Connection, ConnectionPing } from "../lib/api";

export function IntegrationsPage() {
  const { t } = useTranslation();
  const { data: entities, isLoading } = useQuery({
    queryKey: ["legal-entities", false, ""],
    queryFn: () => api.listLegalEntities({}),
  });

  const legalEntityId = entities?.[0]?.id ?? null;

  if (isLoading) {
    return <div className="text-slate-500">{t("app.int_loading")}</div>;
  }

  if (!legalEntityId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        <p>{t("app.int_setup_first")}</p>
        <p className="mt-2 text-xs text-amber-800">
          {t("app.int_setup_first_hint")}
        </p>
      </div>
    );
  }

  return <IntegrationsList legalEntityId={legalEntityId} />;
}

function IntegrationsList({ legalEntityId }: { legalEntityId: string }) {
  const { t } = useTranslation();
  const location = useLocation();
  const highlightedRef = useRef<HTMLLIElement>(null);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const { data, isLoading } = useQuery({
    queryKey: ["connections", legalEntityId],
    queryFn: () => api.listConnections(legalEntityId),
  });
  const [configuring, setConfiguring] = useState<Connection | null>(null);

  // Integrations discoverability hotfix — when arriving via the AI
  // module Rail 2 entries (`/settings/integrations#anthropic` or
  // `#twilio`), scroll the matching card into view and apply a
  // 2-second brand-blue ring highlight. Hash with no matching
  // integration_type = no-op.
  useEffect(() => {
    if (!data?.connections) return;
    const hash = location.hash.replace(/^#/, "");
    if (!hash) return;
    const match = data.connections.find(
      (c) => c.integration_type === hash,
    );
    if (!match) return;
    setHighlightId(hash);
    requestAnimationFrame(() => {
      highlightedRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
    const t = window.setTimeout(() => setHighlightId(null), 2000);
    return () => window.clearTimeout(t);
  }, [location.hash, data?.connections]);

  if (isLoading || !data) {
    return <div className="text-slate-500">{t("app.int_loading")}</div>;
  }

  // Group connections by category for visual organisation.
  const byCategory = data.connections.reduce<Record<string, Connection[]>>(
    (acc, c) => {
      (acc[c.category] ||= []).push(c);
      return acc;
    },
    {},
  );

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-slate-900">{t("app.int_title")}</h1>
        <p className="mt-1 text-sm text-slate-600">
          {t("app.int_subtitle")}
        </p>
      </header>

      {(["messaging", "ai", "government"] as const).map((cat) => {
        const items = byCategory[cat] ?? [];
        if (items.length === 0) return null;
        return (
          <section key={cat}>
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
              {{
                messaging: t("app.int_cat_messaging"),
                ai: t("app.int_cat_ai"),
                government: t("app.int_cat_government"),
              }[cat]}
            </h2>
            <ul className="grid gap-3 sm:grid-cols-2">
              {items.map((c) => (
                <ConnectionCard
                  key={c.integration_type}
                  conn={c}
                  legalEntityId={legalEntityId}
                  onConfigure={() => setConfiguring(c)}
                  liRef={
                    highlightId === c.integration_type
                      ? highlightedRef
                      : undefined
                  }
                  highlighted={highlightId === c.integration_type}
                />
              ))}
            </ul>
          </section>
        );
      })}

      {configuring && (
        <IntegrationConfigureDrawer
          conn={configuring}
          legalEntityId={legalEntityId}
          open
          onOpenChange={(open) => {
            if (!open) setConfiguring(null);
          }}
        />
      )}
    </div>
  );
}

function ConnectionCard({
  conn,
  legalEntityId,
  onConfigure,
  liRef,
  highlighted = false,
}: {
  conn: Connection;
  legalEntityId: string;
  onConfigure: () => void;
  /** Optional ref to the wrapping <li> — used by the discoverability
   * scroll-to-hash flow in IntegrationsList. */
  liRef?: React.RefObject<HTMLLIElement>;
  /** When true, apply the brand-blue 2-second ring highlight. */
  highlighted?: boolean;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [ping, setPing] = useState<ConnectionPing | null>(null);
  const pingMut = useMutation({
    mutationFn: () => api.pingConnection(conn.integration_type, legalEntityId),
    onSuccess: setPing,
  });
  const deleteMut = useMutation({
    mutationFn: () =>
      api.deleteConnection(conn.integration_type, legalEntityId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["connections", legalEntityId] });
      setPing(null);
    },
  });

  return (
    <li
      id={`int-card-${conn.integration_type}`}
      ref={liRef}
      className={[
        "rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition-shadow",
        highlighted ? "ring-2 ring-blue-500 ring-offset-2" : "",
      ].join(" ")}>
      <div className="flex items-start justify-between">
        <div>
          <p className="font-medium text-slate-900">{conn.label}</p>
          <p className="mt-0.5 text-xs text-slate-500">
            {conn.configured
              ? t("app.int_configured_v", { v: conn.key_version })
              : t("app.int_not_configured")}
          </p>
        </div>
        <StatusBadge configured={conn.configured} ping={ping} />
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={onConfigure}
          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
        >
          <Settings className="h-3.5 w-3.5" />
          {conn.configured ? t("app.int_edit") : t("app.int_configure")}
        </button>
        {conn.configured && (
          <>
            <button
              type="button"
              onClick={() => pingMut.mutate()}
              disabled={pingMut.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1 text-xs text-slate-700 hover:bg-slate-100"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              {t("app.int_test")}
            </button>
            <button
              type="button"
              onClick={() => {
                if (confirm(t("app.int_confirm_remove", { name: conn.label }))) {
                  deleteMut.mutate();
                }
              }}
              disabled={deleteMut.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-rose-200 px-2.5 py-1 text-xs text-rose-700 hover:bg-rose-50"
            >
              <Trash2 className="h-3.5 w-3.5" />
              {t("app.int_remove")}
            </button>
          </>
        )}
      </div>

      {ping && (
        <p
          className={`mt-3 text-xs ${
            ping.ok ? "text-emerald-700" : "text-rose-700"
          }`}
        >
          {ping.ok ? "✓" : "✗"} {ping.message}
        </p>
      )}
    </li>
  );
}

function StatusBadge({
  configured,
  ping,
}: {
  configured: boolean;
  ping: ConnectionPing | null;
}) {
  const { t } = useTranslation();
  if (ping) {
    return (
      <span
        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
          ping.ok
            ? "bg-emerald-100 text-emerald-800"
            : "bg-rose-100 text-rose-800"
        }`}
      >
        {ping.ok ? t("app.int_status_healthy") : t("app.int_status_error")}
      </span>
    );
  }
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${
        configured ? "bg-slate-100 text-slate-700" : "bg-amber-100 text-amber-800"
      }`}
    >
      {configured ? t("app.int_status_connected") : t("app.int_status_not_connected")}
    </span>
  );
}

